import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Alert,
} from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

import AccountApi from "../../api_calls/AccountApi";
import PaymentAPI from "../../api_calls/PaymentAPI";
import AppConstants from "../../constants/appConstants";
import { showError } from "../../utils/toastUtils";

const sweetRed = "#e91e63";

const planLabel = (months) => (months === 1 ? "1 month" : `${months} months`);

// Where singles renew. Expired singles are sent here automatically (see the
// 402 handling in api_calls/axiosInstance.js); it's rendered outside the
// profile/subscription guards in layouts/Admin.js so it stays reachable.
export default function RenewSubscription() {
  const [daysLeft, setDaysLeft] = useState(null);
  const [months, setMonths] = useState(1);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    AccountApi.fetchFewUserDetails().then((res) => {
      if (res?.status === "success") {
        setDaysLeft(Number(res.data?.subscription_days) || 0);
      }
    });
  }, []);

  const handlePay = async () => {
    setRedirecting(true);
    try {
      const approvalUrl = await PaymentAPI.createPaypalPayment(months);
      // PayPal sends the user back to /payment/<result> when done.
      window.location.href = approvalUrl;
    } catch {
      setRedirecting(false);
      showError("Could not start the payment. Please try again.");
    }
  };

  const expired = daysLeft !== null && daysLeft <= 0;

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", py: 3, px: 2 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          color: sweetRed,
          mb: 2,
        }}
      >
        <WorkspacePremiumIcon sx={{ mr: 1 }} />
        Your subscription
      </Typography>

      {daysLeft === null ? (
        <CircularProgress size={24} />
      ) : expired ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your subscription has expired. Renew to keep browsing singles,
          chatting, calling and liking profiles. Your profile and settings are
          still available.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have <strong>{daysLeft}</strong> day(s) left. Renewing now adds
          to your remaining days.
        </Alert>
      )}

      <Typography sx={{ mb: 2 }}>Choose a plan:</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {AppConstants.SUBSCRIPTION_PLAN_MONTHS.map((plan) => {
          const selected = plan === months;
          return (
            <Grid item xs={6} sm={3} key={plan}>
              <Card
                variant="outlined"
                sx={{
                  borderColor: selected ? sweetRed : "divider",
                  borderWidth: selected ? 2 : 1,
                }}
              >
                <CardActionArea onClick={() => setMonths(plan)}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {planLabel(plan)}
                    </Typography>
                    <Typography variant="h6" sx={{ color: sweetRed }}>
                      ${AppConstants.SUBSCRIPTION_USD_PER_MONTH * plan}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {plan * 30} days
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Button
        variant="contained"
        size="large"
        onClick={handlePay}
        disabled={redirecting}
        startIcon={redirecting ? <CircularProgress size={18} /> : null}
        sx={{ bgcolor: sweetRed, "&:hover": { bgcolor: "#c2185b" } }}
      >
        Pay ${AppConstants.SUBSCRIPTION_USD_PER_MONTH * months} with PayPal
      </Button>
    </Box>
  );
}
